import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Lock } from 'lucide-react';
import { SpawnRow } from './SpawnRow';
import { accessLabel, mapPath, monsterPath, prettyAccessName } from '../../utils/worldFormat';
import { derived } from '../../utils/worldQueries';
import { MonsterThumb } from './EntityImage';
import { imageEntryForMonster } from '../../utils/entityImages';
import { classifyEntity } from '../../utils/monsterKind';
import type { Monster, Spawn, WorldData, WorldMap } from '../../types/world';
const rowCls = 'flex items-center gap-3 rounded-2xl border border-white/10 bg-[#0A1628] px-4 py-3.5 transition-colors duration-150 hover:border-[#D4AF37]/40';
export function MapList({
  w,
  maps,
  pj
}: {
  w: WorldData;
  maps: WorldMap[];
  pj?: number;
}) {
  const d = derived(w);
  return <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1291" data-mp="1csnvuu0sup8v">
      {maps.map(m => {
      const spots = (w.idx.spawnsByMap.get(m.id) ?? []).filter(s => s.kind === 'spot').length;
      const mons = d.mapMonsters.get(m.id)?.size ?? 0;
      const reachable = pj !== undefined ? m.moveEntries.filter(e => e.minLevel !== undefined && e.minLevel <= pj) : [];
      return <li key={m.id} data-id="element-1292" data-mp="21stgbo05mjz0">
            <Link to={mapPath(m.id)} className={rowCls} data-id="element-1293" data-mp="21t1sfdogdl95">
              <span className="min-w-0 flex-1" data-id="element-1294" data-mp="21umq3c62dkb8">
                <span className="block font-cinzel font-bold text-white" data-id="element-1295" data-mp="1x11xy0r5jkjy">{m.name}</span>
                <span className="mt-0.5 block text-sm text-gray-400" data-id="element-1296" data-mp="2asoxqakazb7h">
                  {accessLabel(m)}
                  {m.moveEntries.length > 1 && ` · ${m.moveEntries.length} accesos`}
                </span>
                <span className="mt-1 block text-xs text-gray-500" data-id="element-1297" data-mp="2kqmk0silqxxg">{spots} spots · {mons} monstruos documentados</span>
                {pj !== undefined && m.moveEntries.length > 1 && <span className="mt-1 block text-xs text-[#D4AF37]/90" data-id="element-1298" data-mp="qcv3d91uvdsi">Con nivel {pj}: {reachable.map(e => prettyAccessName(e.name)).join(', ')}</span>}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1299" data-mp="1b37ffla2se9p" />
            </Link>
          </li>;
    })}
    </ul>;
}
export function MonsterList({
  w,
  monsters,
  npc = false
}: {
  w: WorldData;
  monsters: Monster[];
  npc?: boolean;
}) {
  const d = derived(w);
  return <ul className="grid gap-2.5 md:grid-cols-2" data-id="element-1300" data-mp="1p2qja1vut3g7">
      {monsters.map(m => {
      const maps = Array.from(d.monsterMaps.get(m.id) ?? []).map(id => w.maps.get(id)?.name ?? `Mapa ${id}`);
      const hasLocation = (w.idx.locationsByMonster.get(m.id)?.length ?? 0) > 0;
      return <li key={m.id} className={imageEntryForMonster(w, m.id)?.image ? `${rowCls} !py-2.5` : undefined} data-id="element-1301" data-mp="22td9t6rgnn17">
            {/* La imagen queda fuera del enlace: pulsarla no abre la ficha. El nombre sí. */}
            <MonsterThumb entry={imageEntryForMonster(w, m.id)} name={m.name} data-id="element-1302" data-mp="2csubld3ldgrw" />
            <Link to={monsterPath(m.id)} className={imageEntryForMonster(w, m.id)?.image ? 'flex min-w-0 flex-1 items-center gap-3' : rowCls} data-id="element-1303" data-mp="2hqw2ulb68meg">
              <span className="min-w-0 flex-1" data-id="element-1304" data-mp="65dh35shxmno">
                <span className="flex flex-wrap items-baseline gap-x-2" data-id="element-1305" data-mp="14z73i7c42n0">
                  <span className="font-semibold text-white" data-id="element-1306" data-mp="16sgv6d8v2z8">{m.name}</span>
                  {!npc && m.level !== undefined && <span className="text-xs text-gray-500" data-id="element-1307" data-mp="3sctthjcuy4mz">Nv. monstruo {m.level}</span>}
                  {!npc && classifyEntity(w, m).cls === 'pendiente' && <span className="text-xs text-amber-200/80" data-id="element-1308" data-mp="oujv4fichq35">Pendiente de clasificación</span>}
                </span>
                <span className="mt-0.5 block truncate text-sm text-gray-400" data-id="element-1309" data-mp="jwicfvotzss7">
                  {maps.length ? maps.join(', ') : hasLocation ? '' : 'Ubicación no documentada en los archivos disponibles'}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1310" data-mp="17krqgbfszc8s" />
            </Link>
          </li>;
    })}
    </ul>;
}
export function SpawnList({
  w,
  spawns
}: {
  w: WorldData;
  spawns: Spawn[];
}) {
  return <ul className="grid gap-2.5" data-id="element-1311" data-mp="nb8psod6106p">
      {spawns.map(s => <SpawnRow key={s.id} w={w} s={s} data-id="element-1312" data-mp="sb4u6oprytms" />)}
    </ul>;
}
export function AccessNote({
  count
}: {
  count: number;
}) {
  if (!count) return null;
  return <p className="mb-4 flex items-start gap-2 text-sm text-gray-400" data-id="element-1313" data-mp="dff2qb1tloao">
      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-gray-500" aria-hidden="true" data-id="element-1314" data-mp="ic5jz1pd2pxo" />
      {count} {count === 1 ? 'mapa no tiene' : 'mapas no tienen'} requisito de acceso documentado y no se incluyen al filtrar por nivel de personaje.
    </p>;
}